// Shared category -> lucide-icon mapping, used both by the landing page's
// own category tiles and by ProductCard's image-placeholder fallback (see
// that component's own header comment) — one icon set, so a product's
// placeholder always matches the same tile a buyer would have clicked
// through to find it, rather than a generic, meaningless icon.
//
// Keyed by TOP-LEVEL category name (matching pages/LandingPage.jsx's own
// category tiles, which only ever show parent_id === null categories) — a
// product's own category_id is usually a LEAF category several levels
// under one of these, so resolving a product to an icon means walking UP
// the tree to find its top-level ancestor first (see findTopLevelAncestor
// below), not looking the leaf name up directly.

import { Cpu, HardDrive, Laptop, Package, Printer, Zap } from 'lucide-react';

export const CATEGORY_ICONS = {
  Computers: Laptop,
  'Printers & Scanners': Printer,
  'Storage & Networking': HardDrive,
  'Components & Peripherals': Cpu,
  'EV Accessories': Zap,
};

export const DEFAULT_CATEGORY_ICON = Package;

// Walks `categories` (the flat list GET /public/catalog/categories returns)
// from `categoryId` up through parent_id until it reaches a row with no
// parent — that row IS the top-level category, whether `categoryId` was
// already top-level or several levels down. Returns null if `categoryId`
// isn't found at all (categories still loading, or a stale/deleted id).
export function findTopLevelAncestor(categories, categoryId) {
  if (!categoryId) return null;
  const byId = new Map(categories.map((c) => [c.id, c]));
  let current = byId.get(categoryId);
  const seen = new Set();
  while (current && current.parent_id !== null && !seen.has(current.id)) {
    seen.add(current.id);
    current = byId.get(current.parent_id);
  }
  return current || null;
}

// The icon component for a top-level category name — Package (a generic
// "product" icon) for anything unrecognized, never a broken/missing icon.
export function getCategoryIcon(categoryName) {
  return CATEGORY_ICONS[categoryName] || DEFAULT_CATEGORY_ICON;
}

// Convenience: category id -> icon component in one call, for callers that
// only have the flat `categories` list and a product's `category_id`
// (ProductCard's own callers — pages/LandingPage.jsx, pages/
// CatalogBrowsePage.jsx) rather than an already-resolved category row.
export function getCategoryIconForId(categories, categoryId) {
  const topLevel = findTopLevelAncestor(categories, categoryId);
  return getCategoryIcon(topLevel?.name);
}

// The localized display name for a product's OWN category id — unlike
// getCategoryIconForId above, this does NOT walk up to the top-level
// ancestor: a buyer reading a product card/detail page benefits from the
// specific leaf category ("Laptops"), not the broader tile it sits under
// ("Computers"). Returns null when the id isn't found (categories still
// loading, or a stale id) rather than an empty string, so a caller can
// cleanly omit the line entirely instead of rendering nothing useful.
export function getCategoryNameForId(categories, categoryId, lang) {
  if (!categoryId) return null;
  const category = categories.find((c) => c.id === categoryId);
  if (!category) return null;
  return lang === 'am' && category.name_am ? category.name_am : category.name;
}
